import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Tabs,
  Alert,
  Divider,
} from "antd";
import {
  EditOutlined,
  CalendarOutlined,
  BankOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  debiCheckAmountAdjustmentApi,
  debiCheckCollectionDayChangeApi,
  debiCheckAccountChangeApi,
  debiCheckReactivateMandateApi,
} from "../../../services/Payment/PaymentApis";
import {
  validateAmountAdjustment,
  validateCollectionDayChange,
  validateAccountChange,
  validateMandateStatusChange,
  checkPermission,
  formatApiError,
  formatSuccessMessage,
} from "../../../utils/debiCheckValidation";

const { TextArea } = Input;

const sectionTitleStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 12,
};

interface DebiCheckMaintenanceModalProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
  currentPaymentDetails?: {
    Amount?: number;
    PaymentDate?: number;
    AccountNumber?: string;
    AccountType?: string;
    Bank?: string;
    BranchCode?: string;
    MandateStatus?: string;
  };
  onSuccess?: () => void;
  userRole?: string;
}

const DebiCheckMaintenanceModal: React.FC<DebiCheckMaintenanceModalProps> = ({
  open,
  onClose,
  memberId,
  currentPaymentDetails,
  onSuccess,
  userRole = "",
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("account");

  // Debug logging
  console.log("DebiCheckMaintenanceModal - userRole prop:", userRole);

  // Permission checks based on user roles
  const hasAmountPermission = checkPermission(userRole, "amount");
  const hasCollectionDayPermission = checkPermission(userRole, "collectionDay");
  const hasAccountChangePermission = checkPermission(userRole, "account");
  const hasReactivatePermission = checkPermission(userRole, "reactivate");
  const hasAccountTabPermission =
    hasAmountPermission || hasCollectionDayPermission || hasAccountChangePermission;

  console.log("Permissions:", {
    hasAmountPermission,
    hasCollectionDayPermission,
    hasAccountChangePermission,
    hasReactivatePermission
  });

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (currentPaymentDetails) {
        // NuPay appendix 3.5: 1=Current, 2=Savings
        const mapAccountTypeToCode = (accountType?: string) => {
          const value = String(accountType ?? "").trim().toLowerCase();
          if (
            value === "1" ||
            value === "current" ||
            value === "cheque" ||
            value === "cheque/current"
          ) {
            return "1";
          }
          if (value === "2" || value === "savings") return "2";
          return accountType;
        };

        form.setFieldsValue({
          Amount: currentPaymentDetails.Amount
            ? Number(currentPaymentDetails.Amount)
            : undefined,
          CollectionDay: currentPaymentDetails.PaymentDate
            ? Number(currentPaymentDetails.PaymentDate)
            : undefined,
          AccountNumber: currentPaymentDetails.AccountNumber,
          AccountType: mapAccountTypeToCode(currentPaymentDetails.AccountType),
          Bank: currentPaymentDetails.Bank,
          BranchCode: currentPaymentDetails.BranchCode,
        });
      }
      setActiveTab(hasAccountTabPermission ? "account" : "reactivate");
    }
  }, [open, currentPaymentDetails, form, hasAccountTabPermission]);

  const handleAccountTabSubmit = async (values: any) => {
    if (!hasAccountTabPermission) {
      message.error("You don't have permission to update account details");
      return;
    }

    // NuPay appendix 3.5: 1=Current, 2=Savings
    const mapAccountTypeToCode = (accountType?: string) => {
      const value = String(accountType ?? "").trim().toLowerCase();
      if (
        value === "1" ||
        value === "current" ||
        value === "cheque" ||
        value === "cheque/current"
      ) {
        return "1";
      }
      if (value === "2" || value === "savings") return "2";
      return String(accountType ?? "");
    };

    const amountChanged =
      hasAmountPermission &&
      values.Amount !== undefined &&
      Number(values.Amount) !== Number(currentPaymentDetails?.Amount);

    const collectionDayChanged =
      hasCollectionDayPermission &&
      values.CollectionDay !== undefined &&
      Number(values.CollectionDay) !== Number(currentPaymentDetails?.PaymentDate);

    const accountChanged =
      hasAccountChangePermission &&
      (values.AccountNumber !== currentPaymentDetails?.AccountNumber ||
        mapAccountTypeToCode(values.AccountType) !==
          mapAccountTypeToCode(currentPaymentDetails?.AccountType) ||
        String(values.Bank) !== String(currentPaymentDetails?.Bank) ||
        values.BranchCode !== currentPaymentDetails?.BranchCode);

    if (!amountChanged && !collectionDayChanged && !accountChanged) {
      message.warning("No changes detected. Update at least one field before submitting.");
      return;
    }

    if (amountChanged) {
      const validation = validateAmountAdjustment({
        Amount: values.Amount,
        Reason: values.Reason,
      });
      if (!validation.isValid) {
        validation.errors.forEach((error) => message.error(error));
        return;
      }
    }

    if (collectionDayChanged) {
      const validation = validateCollectionDayChange({
        CollectionDay: values.CollectionDay,
        Reason: values.Reason,
      });
      if (!validation.isValid) {
        validation.errors.forEach((error) => message.error(error));
        return;
      }
    }

    if (accountChanged) {
      const validation = validateAccountChange({
        AccountNumber: values.AccountNumber,
        AccountType: values.AccountType,
        Bank: values.Bank,
        BranchCode: values.BranchCode,
        Reason: values.Reason,
      });
      if (!validation.isValid) {
        validation.errors.forEach((error) => message.error(error));
        return;
      }
    }

    setLoading(true);
    try {
      const successes: string[] = [];

      if (amountChanged) {
        const response = await debiCheckAmountAdjustmentApi(memberId, {
          Amount: values.Amount,
          Reason: values.Reason,
        });
        if (!response.success) {
          message.error(formatApiError(response));
          return;
        }
        successes.push(formatSuccessMessage("amount"));
      }

      if (collectionDayChanged) {
        const response = await debiCheckCollectionDayChangeApi(memberId, {
          CollectionDay: values.CollectionDay,
          Reason: values.Reason,
        });
        if (!response.success) {
          message.error(formatApiError(response));
          return;
        }
        successes.push(formatSuccessMessage("collectionDay"));
      }

      if (accountChanged) {
        const response = await debiCheckAccountChangeApi(memberId, {
          AccountNumber: values.AccountNumber,
          AccountType: values.AccountType,
          Bank: values.Bank,
          BranchCode: values.BranchCode,
          Reason: values.Reason,
        });
        if (!response.success) {
          message.error(formatApiError(response));
          return;
        }
        successes.push(formatSuccessMessage("account"));
      }

      successes.forEach((msg) => message.success(msg));
      form.resetFields();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      message.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateMandate = async (values: any) => {
    if (!hasReactivatePermission) {
      message.error("You don't have permission to reactivate mandates");
      return;
    }

    const validation = validateMandateStatusChange({
      Reason: values.Reason,
    });

    if (!validation.isValid) {
      validation.errors.forEach((error) => message.error(error));
      return;
    }

    setLoading(true);
    try {
      const response = await debiCheckReactivateMandateApi(memberId, {
        Reason: values.Reason,
      });

      if (response.success) {
        message.success(formatSuccessMessage("reactivate"));
        form.resetFields();
        onSuccess?.();
        onClose();
      } else {
        message.error(formatApiError(response));
      }
    } catch (error: any) {
      message.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const getAccountTabFieldNames = () => {
    const fields: string[] = ["Reason"];
    if (hasAmountPermission) fields.push("Amount");
    if (hasCollectionDayPermission) fields.push("CollectionDay");
    if (hasAccountChangePermission) {
      fields.push("AccountNumber", "AccountType", "Bank", "BranchCode");
    }
    return fields;
  };

  const handleSubmit = async () => {
    try {
      const fieldNames =
        activeTab === "account" ? getAccountTabFieldNames() : ["Reason"];
      const values = await form.validateFields(fieldNames);

      switch (activeTab) {
        case "account":
          await handleAccountTabSubmit(values);
          break;
        case "reactivate":
          await handleReactivateMandate(values);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const bankOptions = [
    { label: "SBSA / Standard Bank", value: "1" },
    { label: "Nedbank", value: "2" },
    { label: "FNB", value: "3" },
    { label: "African Bank", value: "7" },
    { label: "Capitec", value: "10" },
    { label: "ABSA", value: "16" },
    { label: "Bidvest Bank", value: "44" },
    { label: "FinBond", value: "55" },
    { label: "TymeBank", value: "61" },
    { label: "Access", value: "Access" },
  ];

  const accountTypeOptions = [
    { label: "Cheque/Current", value: "1" },
    { label: "Savings", value: "2" },
  ];

  const renderAccountTabContent = () => {
    if (!hasAccountTabPermission) {
      return (
        <Alert message="You don't have permission to perform this action" type="warning" />
      );
    }

    const sections: React.ReactNode[] = [];

    if (hasAmountPermission) {
      sections.push(
        <div key="amount">
          <div style={sectionTitleStyle}>
            <EditOutlined style={{ fontSize: 16 }} />
            Amount
          </div>
          <Form.Item
            label="New Amount"
            name="Amount"
            rules={[
              { required: true, message: "Please enter the new amount" },
              { type: "number", min: 0.01, message: "Amount must be greater than 0" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              prefix="R"
              precision={2}
              placeholder="Enter new amount"
            />
          </Form.Item>
        </div>
      );
    }

    if (hasCollectionDayPermission) {
      sections.push(
        <div key="collectionDay">
          <div style={sectionTitleStyle}>
            <CalendarOutlined style={{ fontSize: 16 }} />
            Collection Day
          </div>
          <Form.Item
            label="Collection Day"
            name="CollectionDay"
            rules={[
              { required: true, message: "Please select a collection day" },
              { type: "number", min: 1, max: 31, message: "Day must be between 1 and 31" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={31}
              placeholder="Enter day (1-31)"
            />
          </Form.Item>
        </div>
      );
    }

    if (hasAccountChangePermission) {
      sections.push(
        <div key="account">
          <div style={sectionTitleStyle}>
            <BankOutlined style={{ fontSize: 16 }} />
            Account
          </div>
          <Alert
            message="Account Authentication Required"
            description="After submitting, the customer will need to authenticate the account change via their banking app."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            label="Account Number"
            name="AccountNumber"
            rules={[
              { required: true, message: "Please enter account number" },
              { pattern: /^\d{9,11}$/, message: "Account number must be 9-11 digits" },
            ]}
          >
            <Input placeholder="Enter account number" />
          </Form.Item>
          <Form.Item
            label="Account Type"
            name="AccountType"
            rules={[{ required: true, message: "Please select account type" }]}
          >
            <Select placeholder="Select account type" options={accountTypeOptions} />
          </Form.Item>
          <Form.Item
            label="Bank"
            name="Bank"
            rules={[{ required: true, message: "Please select a bank" }]}
          >
            <Select placeholder="Select bank" options={bankOptions} showSearch />
          </Form.Item>
          <Form.Item
            label="Branch Code"
            name="BranchCode"
            rules={[
              { required: true, message: "Please enter branch code" },
              { pattern: /^\d{6}$/, message: "Branch code must be 6 digits" },
            ]}
          >
            <Input placeholder="Enter 6-digit branch code" maxLength={6} />
          </Form.Item>
        </div>
      );
    }

    return (
      <div
        style={{
          padding: 20,
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          backgroundColor: "#fafafa",
        }}
      >
        <Form form={form} layout="vertical">
          {sections.map((section, index) => (
            <div key={(section as React.ReactElement).key}>
              {index > 0 && <Divider />}
              {section}
            </div>
          ))}
          <Divider />
          <Form.Item
            label="Reason"
            name="Reason"
            rules={[
              { required: true, message: "Please provide a reason" },
              { max: 255, message: "Reason must be less than 255 characters" },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="e.g., Premium increase due to policy upgrade"
            />
          </Form.Item>
        </Form>
      </div>
    );
  };

  const tabItems = [
    ...(hasAccountTabPermission
      ? [
          {
            key: "account",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                <BankOutlined style={{ fontSize: 16 }} />
                Account
              </span>
            ),
            children: renderAccountTabContent(),
          },
        ]
      : []),
    ...(hasReactivatePermission
      ? [
          {
            key: "reactivate",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px" }}>
                <PlayCircleOutlined style={{ fontSize: "16px" }} />
                Reactivate
              </span>
            ),
            children: (
              <div style={{ padding: "20px", border: "1px solid #d9d9d9", borderRadius: "4px", backgroundColor: "#fafafa" }}>
                <Alert
                  message="Reactivate Mandate"
                  description="Reactivating the mandate will resume collections on the next due date."
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form form={form} layout="vertical">
                  <Form.Item
                    label="Reason"
                    name="Reason"
                    rules={[
                      { required: true, message: "Please provide a reason" },
                      { max: 255, message: "Reason must be less than 255 characters" },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="e.g., Financial situation resolved"
                    />
                  </Form.Item>
                </Form>
              </div>
            ),
          },
        ]
      : []),
  ];


  return (
    <Modal
      title="DebiCheck Mandate Maintenance"
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Submit
        </Button>,
      ]}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        type="card"
        style={{ marginTop: 16 }}
        tabBarStyle={{ border: 'none', marginBottom: 16 }}
        tabBarGutter={8}
      />
    </Modal>
  );
};

export default DebiCheckMaintenanceModal;
